/**
 * `<NodeHistoryView/>` — the drawer's History tab: a per-task
 * timeline-of-updates (B3.1, rebuilt to the 2026-05-25 Claude-design A1 rail).
 *
 * Issue #763 (the seam) → the per-task timeline feature.
 *
 * Layout, top to bottom:
 *   1. the B3.2 `StateTimeline` strip — "how THIS task moved over time" (always
 *      the focal node, the calm anchor of the tab);
 *   2. a scope toggle + an "open full view" affordance;
 *   3. the day-grouped, **newest-first** rail (`TaskTimeline`, compact).
 *
 * Scope (the founder's headline behaviour): the feed defaults to "This task &
 * sub-tasks" — every event below this node in the tree, merged — with a
 * one-click narrowing to "This task only". The state strip stays focal-node
 * only either way (it's about this task's lifecycle); in subtree scope a
 * descendant event is tagged "on «sub-task»".
 *
 * Pure view (§A7 query/view split): it consumes `events` + `isLoading` +
 * `isError` and the `scope` it should show; it never calls `apiFetch`. The
 * fetch + the scope state live in the drawer (one `useNodeHistory` source).
 *
 * Brand firewall (§A9 — sharpest on this surface): every line is provenance
 * about *the task* ("Moved to Done", "Assigned Dr. Wong"). There is NO
 * aggregation about a person — no count, rate, or ranking — anywhere.
 *
 * Degradation: loading → "Loading activity…"; error/404 (the endpoint is base
 * infra but may be undeployed) → "History isn’t available here yet."; empty →
 * "No activity yet." — never a crash.
 */

import type { HistoryScope, NodeHistoryItem } from "../api/useNodeHistory";
import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import { PALETTE } from "../tree/palette";
import { StateTimeline } from "./StateTimeline";
import { LONG_HISTORY_THRESHOLD, TaskTimeline } from "./TaskTimeline";
import { TimelineScopeToggle } from "./TimelineScopeToggle";

export interface NodeHistoryViewProps {
  /** The focal node title — for the state-timeline header. */
  nodeTitle: string;
  /** The focal node id — events for OTHER nodes are tagged in subtree scope. */
  nodeId: string;
  /** The events to render (focal-only or subtree, per `scope`), from the hook. */
  events: NodeHistoryItem[];
  isLoading: boolean;
  isError: boolean;
  /** Team roster, to resolve actor + referenced-member names. */
  members: TaskMember[];
  /** node id → title, for "created under '…'", dependency names, subtree tags. */
  nodeTitleById?: Map<string, string> | undefined;
  /** The current scope (drives both the fetch — in the drawer — and the tag). */
  scope: HistoryScope;
  onScopeChange: (scope: HistoryScope) => void;
  /** Open the prominent full-panel timeline for this node (omit to hide). */
  onOpenFullView?: (() => void) | undefined;
  /** Team IANA zone for day bucketing + timestamps. */
  teamTimezone?: string | undefined;
}

export function NodeHistoryView(props: NodeHistoryViewProps) {
  const {
    events,
    isLoading,
    isError,
    members,
    nodeTitleById,
    nodeTitle,
    nodeId,
    scope,
    onScopeChange,
    onOpenFullView,
    teamTimezone,
  } = props;

  const theme = useMapTheme();
  const editorial = theme.mode === "editorial";
  const c = theme.palette;

  // The state strip is always the focal node's lifecycle; in subtree scope the
  // loaded stream is wider, so filter to this node for the strip.
  const focalEvents = scope === "subtree" ? events.filter((e) => e.node_id === nodeId) : events;

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <TimelineScopeToggle scope={scope} onChange={onScopeChange} compact />
      {onOpenFullView && (
        <button
          type="button"
          data-testid="timeline-open-full"
          onClick={onOpenFullView}
          className="rounded-md px-2 py-1 text-[11px] font-semibold transition-opacity hover:opacity-80"
          style={{ color: editorial ? c.teal : PALETTE.teal }}
        >
          ⤢ Full view
        </button>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <HistoryMessage testId="history-loading" glyph="🕑" title="Loading activity…">
        A calm record of what changed and when.
      </HistoryMessage>
    );
  }

  // The endpoint is base temporal infra (not component-gated), but may be
  // undeployed in some environments — degrade calmly, never crash.
  if (isError) {
    return (
      <HistoryMessage
        testId="history-unavailable"
        glyph="🕑"
        title="History isn’t available here yet."
      >
        The story of this task will appear here once it’s switched on.
      </HistoryMessage>
    );
  }

  return (
    <div data-testid="node-history-view" className="flex flex-col gap-4">
      {/* B3.2 — the compact "how this task moved" strip (focal node). */}
      <StateTimeline nodeTitle={nodeTitle} events={focalEvents} />

      {header}

      {events.length === 0 ? (
        <HistoryMessage testId="history-empty" glyph="🕑" title="No activity yet.">
          {scope === "subtree"
            ? "As this task and its sub-tasks change, their story shows up here."
            : "As the team updates this task, its story shows up here."}
        </HistoryMessage>
      ) : (
        <TaskTimeline
          events={events}
          members={members}
          nodeTitleById={nodeTitleById}
          focalNodeId={nodeId}
          showNodeAttribution={scope === "subtree"}
          compact
          collapsibleDays={events.length > LONG_HISTORY_THRESHOLD}
          tz={teamTimezone}
        />
      )}

      {/* The footer forks copy in editorial to the wireframe B3.1 framing —
          "Reads as a story of the work — never a tally of a person." (the
          brand firewall, §A9). Legacy keeps the shipped scope line, byte-
          identical. */}
      <div
        className="border-t pt-3 text-[11px] italic"
        style={{
          borderColor: editorial ? c.rule : PALETTE.cardBorder,
          color: editorial ? c.faint : PALETTE.faint,
          ...(editorial ? { fontFamily: theme.fonts.mono, letterSpacing: "0.02em" } : {}),
        }}
        data-testid="history-footer"
      >
        {editorial
          ? "Reads as a story of the work — never a tally of a person."
          : scope === "subtree"
            ? "showing the life of this task and its sub-tasks"
            : "showing the life of this task"}
      </div>
    </div>
  );
}

// ── Calm message states (loading / error / empty) ────────────────────────────

function HistoryMessage({
  testId,
  glyph,
  title,
  children,
}: {
  testId: string;
  glyph: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col items-center justify-center gap-2 py-10 text-center"
    >
      <span className="text-2xl" aria-hidden>
        {glyph}
      </span>
      <div className="text-sm font-semibold" style={{ color: PALETTE.slate }}>
        {title}
      </div>
      <div className="max-w-[18rem] text-xs" style={{ color: PALETTE.faint }}>
        {children}
      </div>
    </div>
  );
}
